import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { func, bool, arrayOf, shape, string, number, oneOfType, oneOf } from "prop-types";
import { push } from "react-router-redux";

import { actions as servicePaymentsActions, selectors as servicePaymentsSelectors } from "reducers/servicePayments";
import { actions as paymentsAFIPActions, selectors as paymentsAFIPSelectors } from "reducers/paymentsAFIP";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as notificationActions } from "reducers/notification";
import { actions as accountsActions, selectors as accountsSelector } from "reducers/accounts";
import { actions as statusActions, selectors as statusSelectors } from "reducers/status";

import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { FORM_ID } from "constants/servicePayments";

import Col from "react-bootstrap/lib/Col";
import Table from "pages/_components/Table";
import Head from "pages/_components/Head";
import Button from "pages/_components/Button";
import ContextMenu from "pages/_components/ContextMenu";
import Notification from "pages/_components/Notification";
import I18n from "pages/_components/I18n";
import Image from "pages/_components/Image";
import PageLoading from "pages/_components/PageLoading";
import DrawerModal from "pages/_components/drawer/DrawerModal";
import InfiniteScrollSentinel from "pages/_components/InfiniteScrollSentinel";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";
import { isEligibleServicePaymentsExitHref, resolveServicePaymentsExitHref } from "util/servicePaymentsExitNavigation";

import { agendaPagoShape } from "util/servicePayments";

import AFIPServiceConditions from "pages/enabledContributors/_components/AFIPServiceConditions";
import PaymentAFIPList from "pages/servicePayments/_components/PaymentAFIPList";
import classNames from "classnames";
import { saveLocation } from "util/transaction";
import { resizableRoute } from "pages/_components/Resizable";
import Container from "pages/_components/Container";
import AgendaPagoItem from "./_components/AgendaPagoItem";
import MultiplePaymentsList from "./multiple/MultiplePaymentsList";
import MultipleAFIPList from "./multiple/MultipleAFIPList";

class ServicePaymentsMain extends Component {
    static propTypes = {
        dispatch: func.isRequired,
        isFetching: bool,
        servicePaymentsSelected: bool.isRequired,
        agendaPagos: arrayOf(agendaPagoShape).isRequired,
        flatPayments: arrayOf(shape({})),
        multiplePaymentsInfo: arrayOf(shape({})),
        isDesktop: bool.isRequired,
        isSmallDesktop: bool.isRequired,
        fetchingPayOrDelete: bool.isRequired,
        activeEnvironment: shape({
            permissions: shape({
                newPayment: bool,
            }),
        }).isRequired,
        accounts: arrayOf(
            shape({
                idProduct: string.isRequired,
            }),
        ).isRequired,
        isRetailEnvironment: bool,
        moduleEntryPath: oneOfType([string, oneOf([null])]),
        lastPathname: string.isRequired,
        totalPages: number.isRequired,
        pageNumber: number.isRequired,
        embeddedInV3Shell: bool,
        location: shape({
            pathname: string.isRequired,
        }).isRequired,
    };

    static defaultProps = {
        isFetching: false,
        isRetailEnvironment: false,
        flatPayments: [],
        multiplePaymentsInfo: [],
        embeddedInV3Shell: false,
        moduleEntryPath: null,
    };

    state = {
        showDrawer: false,
    };

    componentDidMount() {
        const { dispatch, isDesktop, multiplePaymentsInfo, location, lastPathname, moduleEntryPath } = this.props;
        saveLocation(this.props);

        dispatch(accountsActions.listAccounts());

        if (!multiplePaymentsInfo?.length) {
            dispatch(servicePaymentsActions.listAgendaPagos(isDesktop));
        }
        dispatch(paymentsAFIPActions.getToken());
        dispatch(paymentsAFIPActions.cleanBackFromOtherPage());
        if (
            moduleEntryPath == null &&
            isEligibleServicePaymentsExitHref(lastPathname) &&
            lastPathname !== location.pathname
        ) {
            dispatch(servicePaymentsActions.setModuleEntryPath(lastPathname));
        }
        this.syncEmbeddedV3ShellLayout();
    }

    componentDidUpdate(prevProps) {
        const { embeddedInV3Shell, isDesktop, lastPathname, moduleEntryPath } = this.props;
        if (
            embeddedInV3Shell &&
            (prevProps.embeddedInV3Shell !== embeddedInV3Shell ||
                prevProps.isDesktop !== isDesktop ||
                prevProps.lastPathname !== lastPathname ||
                prevProps.moduleEntryPath !== moduleEntryPath)
        ) {
            this.syncEmbeddedV3ShellLayout();
        }
    }

    componentWillUnmount() {
        const { dispatch, servicePaymentsSelected, embeddedInV3Shell, location } = this.props;
        if (embeddedInV3Shell) {
            clearEmbeddedV3ShellLayoutState(dispatch);
        }
        dispatch(servicePaymentsActions.setService(servicePaymentsSelected));
        dispatch(statusActions.saveLastHref(location));
    }

    resolveExitHref = () => {
        const { moduleEntryPath, lastPathname, location } = this.props;
        return resolveServicePaymentsExitHref({
            moduleEntryPath,
            lastPathname,
            currentPathname: location.pathname,
        });
    };

    syncEmbeddedV3ShellLayout = () => {
        const { dispatch, embeddedInV3Shell, isDesktop } = this.props;
        const newLabel = configUtils.getBoolean("frontend.Show.ContextualMenu.Functionalities", false);
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["servicePayments"],
            titleKey: `menu.payments.payServices${newLabel ? ".arca" : "Afip"}`,
            goBackPath: this.resolveExitHref(),
            rightContentMobile: !isDesktop ? this.rightContent() : undefined,
        });
    };

    handleBack = () => {
        const { dispatch } = this.props;
        const target = this.resolveExitHref();
        dispatch(push(target));
        dispatch(servicePaymentsActions.setModuleEntryPath(null));
    };

    /**
     * Starts a new service payment. The payment categories are requested from this screen so the
     * user only navigates to `/newPayment` when the Link service answers successfully; on failure
     * the saga shows the error notification and the user stays here.
     */
    handleNewPayment = () => {
        const { dispatch, accounts, location } = this.props;
        if (accounts && accounts.length > 0) {
            dispatch(servicePaymentsActions.cleanSummary());
            dispatch(statusActions.saveLastHref(location));
            dispatch(servicePaymentsActions.listRubros(accounts[0], { successPath: "/newPayment" }));
        } else {
            dispatch(
                notificationActions.showNotification(i18nUtils.get("newPayment.noAccounts.error"), "error", [
                    "servicePayments",
                ]),
            );
        }
    };

    allowMultiplePayments = () => {
        const { isRetailEnvironment } = this.props;

        const showMultiplePaymentsForCorporate = configUtils.getBoolean(
            "frontend.show.MultiplePaymentTAX.functionalities.Corporate",
            false,
        );
        const showMultiplePaymentsForRetail = configUtils.getBoolean(
            "frontend.show.MultiplePaymentTAX.functionalities.Retail",
            false,
        );

        return isRetailEnvironment ? showMultiplePaymentsForRetail : showMultiplePaymentsForCorporate;
    };

    closeDrawer = () => {
        this.setState({ showDrawer: false });
    };

    rightContent = () => {
        const { isDesktop, activeEnvironment, dispatch } = this.props;
        const newlabel = configUtils.getBoolean("frontend.Show.ContextualMenu.Functionalities", false);
        if (isDesktop) {
            return (
                <div className="d-flex align-items-center gap-2">
                    {activeEnvironment.permissions.newPayment ? (
                        <Button label={`${FORM_ID}.newPayment`} bsStyle="primary" onClick={this.handleNewPayment} />
                    ) : (
                        undefined
                    )}
                    <Button
                        block={false}
                        label={`${FORM_ID}.attachedServices`}
                        onClick={() => {
                            dispatch(push("/adhesiones/list"));
                        }}
                        bsStyle="outline"
                    />
                    <ContextMenu
                        styleContext={{ justifyContent: "flex-end" }}
                        dropBtnClass="pl-md-3 pr-md-0"
                        items={[
                            {
                                label: `${FORM_ID}.${newlabel ? "arca" : "afip"}Services`,
                                onClick: () => {
                                    this.setState({ showDrawer: true });
                                },
                                icon: "Arca",
                            },
                            {
                                label: "menu.payments.enabledContributors",
                                onClick: () => {
                                    dispatch(push(`/payments/enabledContributors`));
                                },
                                icon: "UserCheck",
                            },
                        ]}
                    />
                </div>
            );
        }

        const items = [];

        if (activeEnvironment.permissions.newPayment) {
            items.push({
                label: `${FORM_ID}.newPayment`,
                onClick: () => this.handleNewPayment(),
                icon: "PayService",
            });
        }

        items.push(
            {
                label: `${FORM_ID}.attachedServices`,
                onClick: () => {
                    dispatch(push("/adhesiones/list"));
                },
                icon: "AddServices",
            },
            {
                label: `${FORM_ID}.${newlabel ? "arca" : "afip"}Services`,
                onClick: () => {
                    this.setState({ showDrawer: true });
                },
                icon: "Arca",
            },
            {
                label: "menu.payments.enabledContributors",
                onClick: () => {
                    dispatch(push(`/payments/enabledContributors`));
                },
                icon: "UserCheck",
            },
        );

        return (
            <div>
                <ContextMenu styleContext={{ justifyContent: "flex-end" }} color="white" items={items} />
            </div>
        );
    };

    changeOption = (option) => {
        const { dispatch, isDesktop } = this.props;

        if (option === "pay.services") {
            dispatch(servicePaymentsActions.setServiceSelected());
            dispatch(servicePaymentsActions.cleanUp());
            dispatch(servicePaymentsActions.listAgendaPagos(isDesktop));
            dispatch(paymentsAFIPActions.getToken());
        } else {
            dispatch(servicePaymentsActions.setPaymentSelected());
        }
    };

    rowButtons = () => {
        const { isDesktop, servicePaymentsSelected } = this.props;
        const newLabel = configUtils.getBoolean("frontend.Show.ContextualMenu.Functionalities", false);
        return (
            <Container
                className={classNames("align-items-center container-white py-1", {
                    "mt-2": isDesktop,
                })}>
                <Col xs={12} className="d-flex py-2 px-1 justify-content-center">
                    <div className="tablet-selector-button my-1 mx-1">
                        <Button
                            className={classNames("btn-outline btn-regular chip-selector-btn", {
                                selected: servicePaymentsSelected,
                                "py-2 px-0 m-0 pl-2": !isDesktop,
                            })}
                            block={false}
                            image={servicePaymentsSelected ? "images/check.svg" : ""}
                            onClick={() => this.changeOption("pay.services")}
                            label={`${FORM_ID}.services.label`}
                        />
                    </div>
                    <div className="tablet-selector-button my-1 ml-1">
                        <Button
                            className={classNames("btn-outline btn-regular chip-selector-btn", {
                                selected: !servicePaymentsSelected,
                                "py-2 px-0 m-0 pl-2": !isDesktop,
                            })}
                            block={false}
                            image={servicePaymentsSelected ? "" : "images/check.svg"}
                            onClick={() => this.changeOption("pay.afip")}
                            label={`${FORM_ID}.${newLabel ? "ARCA" : "AFIP"}.label`}
                        />
                    </div>
                </Col>
            </Container>
        );
    };

    allowMultipleAFIP = () => {
        const { isRetailEnvironment } = this.props;
        const showMultipleAFIPForCorporate = configUtils.getBoolean(
            "frontend.show.MultiplePaymentVEP.functionalities.Corporate",
            false,
        );
        const showMultipleAFIPForRetail = configUtils.getBoolean(
            "frontend.show.MultiplePaymentVEP.functionalities.Retail",
            false,
        );

        return isRetailEnvironment ? showMultipleAFIPForRetail : showMultipleAFIPForCorporate;
    };

    fetchNextPage = (pn) => {
        const { dispatch, pageNumber, totalPages, isDesktop, isFetching } = this.props;
        if (isFetching) {
            return;
        }
        const targetPage = pn ?? pageNumber + 1;
        if (targetPage > totalPages || targetPage < 1 || targetPage <= pageNumber) {
            return;
        }
        const appendNextPage = targetPage === pageNumber + 1;
        dispatch(servicePaymentsActions.setPageNumber(targetPage));
        dispatch(servicePaymentsActions.listAgendaPagos(appendNextPage ? false : isDesktop));
    };

    renderAgendaPagoItems = () => {
        const { isDesktop, isSmallDesktop, agendaPagos } = this.props;
        const currencies = [{ id: 0, label: i18nUtils.get(`currency.label.0`) }];

        return (
            agendaPagos &&
            agendaPagos.length > 0 &&
            agendaPagos.map((agendaPago) => (
                <AgendaPagoItem
                    key={`transaction-${agendaPago.ususerLP}`}
                    agendaPago={agendaPago}
                    isDesktop={isDesktop}
                    isSmallDesktop={isSmallDesktop}
                    tableLabel={FORM_ID}
                    currencies={currencies}
                />
            ))
        );
    };

    renderEmptyAgendaPagos = () => {
        const { isDesktop } = this.props;

        return (
            <div className={`text-center no-more-data ${!isDesktop} mx-4`} key="noMoreMovements">
                <div className="illustration-wrapper">
                    <Image
                        src="images/coloredIcons/no-debts.svg"
                        className="svg-big-icon"
                        styles={{ width: isDesktop ? "96px" : "72px", height: "auto" }}
                    />
                </div>
                <p
                    className="text-lead"
                    style={{
                        opacity: 1,
                        marginTop: isDesktop && "2rem",
                        fontFamily: !isDesktop && "Graphik-Light",
                        color: !isDesktop && "#707070",
                    }}>
                    <I18n id={`${FORM_ID}.empty.message`} />
                </p>
            </div>
        );
    };

    renderAgendaPagosInfiniteList = () => {
        const { isDesktop, totalPages, pageNumber, isFetching } = this.props;
        const items = this.renderAgendaPagoItems();
        const hasMore = pageNumber < totalPages;
        const endOfListItem = (
            <p className="text-center" key="noMoreAgendaPagos">
                <I18n id="menu.payments.mobile.empty.message" />
            </p>
        );

        return (
            <>
                {isDesktop ? <Table>{items}</Table> : items}
                <InfiniteScrollSentinel hasMore={hasMore} isLoading={isFetching} onLoadMore={this.fetchNextPage} />
                {!hasMore && endOfListItem}
            </>
        );
    };

    renderAgendaPagosContent = () => {
        const { agendaPagos } = this.props;
        const hasAgendaPagos = agendaPagos !== undefined && agendaPagos.length > 0;

        if (!hasAgendaPagos) {
            return this.renderEmptyAgendaPagos();
        }

        return this.renderAgendaPagosInfiniteList();
    };

    renderList = () => {
        const { isDesktop, isSmallDesktop, servicePaymentsSelected, agendaPagos } = this.props;

        if (servicePaymentsSelected) {
            return (
                <>
                    <div
                        className={`flex-grow scrollable ${!isDesktop &&
                            !agendaPagos &&
                            !agendaPagos.length &&
                            "no-results-container"}`}>
                        <Col className="col col-12 px-0">
                            <div
                                className={`w-100 ${
                                    !isDesktop ? "d-flex bd-spacing-0-2 flex-column" : "bd-spacing-0-8"
                                }`}
                                ref={(tableRef) => {
                                    this.child = tableRef;
                                }}>
                                {this.renderAgendaPagosContent()}
                            </div>
                        </Col>
                    </div>
                </>
            );
        }

        if (this.allowMultipleAFIP()) {
            return (
                <>
                    <MultipleAFIPList isDesktop={isDesktop} isSmallDesktop={isSmallDesktop} />
                </>
            );
        }

        return (
            <>
                <PaymentAFIPList isDesktop={isDesktop} isSmallDesktop={isSmallDesktop} />
            </>
        );
    };

    renderContent() {
        const { showDrawer } = this.state;
        const {
            isDesktop,
            isSmallDesktop,
            isFetching,
            fetchingPayOrDelete,
            pageNumber,
            flatPayments,
            agendaPagos,
            embeddedInV3Shell,
        } = this.props;
        const loading = !!(isFetching || fetchingPayOrDelete);
        const hasCachedData = flatPayments?.length > 0 || agendaPagos?.length > 0;
        const newLabel = configUtils.getBoolean("frontend.Show.ContextualMenu.Functionalities", false);
        return (
            <>
                {!embeddedInV3Shell && <Notification scopeToShow="servicePayments" />}
                {showDrawer && (
                    <DrawerModal
                        content={<AFIPServiceConditions closeDrawer={this.closeDrawer} isDesktop={isDesktop} />}
                        width={isDesktop ? "600px" : "100%"}
                        showDrawerRemote={showDrawer}
                        closeRemotely={() => this.closeDrawer()}
                        backButton={!isDesktop}
                        title={!isDesktop ? `payments.service${newLabel ? "ARCA" : "AFIP"}.title` : undefined}
                        headerClassName={!isDesktop ? "blue-main-header-mobile" : undefined}
                        contentClassName="afip-conditions-modal"
                    />
                )}
                {(!embeddedInV3Shell || isDesktop) && (
                    <div className="admin-detail-head px-0 mb-0">
                        {isDesktop ? <Head onBack={this.handleBack} /> : undefined}
                        <Head
                            title={`menu.payments.payServices${newLabel ? ".arca" : "Afip"}`}
                            rightContent={this.rightContent}
                            isFetchingExport={false}
                            onBack={!isDesktop ? this.handleBack : undefined}
                            headerClassName={
                                !isDesktop ? "blue-main-header-mobile servive-payment-title-mobile" : undefined
                            }
                        />
                    </div>
                )}
                {this.rowButtons()}
                <PageLoading className="line-loader" loading={pageNumber === 1 && loading && !hasCachedData}>
                    {this.allowMultiplePayments() ? (
                        <MultiplePaymentsList
                            isDesktop={isDesktop}
                            isSmallDesktop={isSmallDesktop}
                            fetchNextPage={this.fetchNextPage}
                        />
                    ) : (
                        this.renderList()
                    )}
                </PageLoading>
            </>
        );
    }

    render() {
        return this.renderContent();
    }
}

const mapStateToProps = (state) => ({
    isFetching: servicePaymentsSelectors.getFetching(state),
    fetchingPayOrDelete: paymentsAFIPSelectors.getFetchingPayOrDelete(state),
    servicePaymentsSelected: servicePaymentsSelectors.getServiceSelected(state),
    agendaPagos: servicePaymentsSelectors.getAgendaPagos(state),
    flatPayments: servicePaymentsSelectors.getFlatPayments(state),
    multiplePaymentsInfo: servicePaymentsSelectors.getMultiplePaymentsInfo(state),
    isBackFromOtherPage: paymentsAFIPSelectors.getIsBackFromOtherPage(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    accounts: accountsSelector.getAccounts(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    lastPathname: statusSelectors.getPathname(state),
    moduleEntryPath: servicePaymentsSelectors.getModuleEntryPath(state),
    pageNumber: servicePaymentsSelectors.getPageNumber(state),
    totalPages: servicePaymentsSelectors.getTotalPages(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(ServicePaymentsMain);
